/**
 * Merged cells and authored column widths inside an `.xlsx` — the other half of "show the excel
 * as it is" that `read-excel-file` never surfaces, because it only ever hands back cell VALUES,
 * not the sheet's layout metadata (`xlsxPreview.ts` renders those values; this module is what lets
 * it render them shaped the way the source actually looks).
 *
 * Verified against REAL customer requisition workbooks, not assumed — this is what sent us
 * looking here in the first place: a purchaser's complaint that the xlsx preview "creates lots of
 * space and indentation error" turned out to have a precise, inspectable cause. Downloading and
 * unzipping three real live-bench attachments showed:
 *   - `REQ N 1 Electrical Requsition for store.xlsx` and a `DTA-10 DECK REQUISITION` file both
 *     have a merge-heavy label/value preamble (`A1:B1` "Ship Name", `F1:I1` its value, …) — but
 *     `xlsxPreview.ts`'s existing header-block handling (join each row's non-blank cells into one
 *     text line) happens to read fine WITHOUT merge support, because a merged cell's "swallowed"
 *     siblings are simply empty in the underlying XML (OOXML only ever stores a value in a
 *     merge's anchor cell) — so no visible bug there.
 *   - `FORM 19-069 BEDSHEET & PILLOW.xlsx` is the one that actually shows the complaint: **72**
 *     `<mergeCell>`s in its label/value preamble, spread across up to column `AL`, none of which
 *     forms a single "dense" row (see `xlsxPreview.ts`'s `isDenseRow`) because each row's real
 *     content is 3-4 merged fields scattered across a ~16-38-column-wide sheet. With no dense row
 *     found, the WHOLE sheet falls into the plain data-grid path instead of the header-flatten
 *     path — and rendered as a literal one-cell-per-column grid, a `C2:F2` value merge shows up as
 *     one populated cell followed by three blank `<td>`s, every subsequent column on that row
 *     visibly shifted right of where its label sits. That is the "indentation error": not a CSS
 *     bug, a missing-merge bug. The same file also has a genuine leading blank row (`row 1`, a
 *     `ht="4.5"` spacer with no cell values at all — a coloured bar in Excel, not data) that the
 *     grid rendered as a visible empty row for the same reason: nothing was trimming a LEADING
 *     blank row (only trailing ones were ever trimmed).
 *
 * So: this module extracts, per sheet, the `<mergeCells>` OOXML carries and the authored
 * `<cols customWidth="1">` widths, and `xlsxPreview.ts` uses them to (a) collapse a merge into one
 * `colSpan`/`rowSpan` cell instead of one-populated-plus-N-blank, and (b) size columns close to
 * how the sheet's author actually set them rather than raw browser auto-layout. Leading/trailing
 * blank-row and blank-column trimming lives in `xlsxPreview.ts` itself (it's a property of the
 * cell VALUES, not the layout metadata this module reads) but is motivated by the same real file.
 *
 * ---- Why a third independent zip walk ----
 *
 * `read-excel-file` (cell values) and `xlsxImages.ts` (embedded images) already each unzip the
 * same fetched bytes independently — see `xlsxImages.ts`'s header for why that's the right
 * trade-off here (small, machine-generated, always-well-formed XML; a full parser buys nothing
 * extra). This module is a third walk of the same kind, for the same reason, sharing
 * `xlsxImages.ts`'s `findTags`/`attr` regex helpers and its `resolveWorksheetPaths` (sheet name →
 * worksheet zip path — the one relationship hop both modules need, the ONLY hop this one needs,
 * since `<mergeCells>` and `<cols>` live directly in the worksheet's own XML with no further
 * indirection).
 */

import { attr, findTags, resolveWorksheetPaths } from './xlsxImages'

/** One merged region, anchored at its top-left cell — 0-based, in the ORIGINAL (untrimmed) sheet's
 * own row/column coordinates, exactly as `<mergeCell ref="F1:I1"/>` describes it. `xlsxPreview.ts`
 * translates these into its own trimmed coordinate space (see `buildSheetPreview`) — this module
 * doesn't know about trimming at all, it just reports what the sheet's XML says. */
export interface XlsxMerge {
  row: number
  col: number
  /** Always >= 1; a `rowSpan` of 1 means the merge is purely horizontal. */
  rowSpan: number
  /** Always >= 1; a `colSpan` of 1 means the merge is purely vertical. */
  colSpan: number
}

export interface XlsxSheetLayout {
  merges: XlsxMerge[]
  /** 0-based original column index → authored width, in Excel's own character-count unit (the
   * `width` attribute on `<col customWidth="1">` — roughly "how many `0`-glyphs of the sheet's
   * default font fit"). Only columns the AUTHOR actually widened/narrowed are present — a `<col>`
   * entry Excel wrote for styling alone (no `customWidth="1"`) is intentionally excluded, or every
   * column at the sheet's default width would get treated as "authored" and this would stop being
   * a signal. `charWidthToPx` converts a value from this map to a CSS pixel width. */
  columnWidths: Map<number, number>
}

export type XlsxLayoutExtraction =
  | { status: 'ok'; layouts: Map<string, XlsxSheetLayout> }
  | { status: 'scan_failed'; detail: string }

/** How far right a `<cols min max>` range is allowed to widen the `columnWidths` map. A sheet can
 * legitimately declare `max="16384"` (Excel's column ceiling) for a single default-width run —
 * harmless in Excel, but filling a `Map` with that many entries for width metadata no real sheet
 * this preview ever renders that many columns of is pure waste. Every real sheet this was
 * verified against uses well under this many columns; a genuinely wider one just loses width
 * data past this point, never crashes or slows down. */
const MAX_TRACKED_COLUMN_INDEX = 999

/** Spreadsheet column letters → 0-based index — the inverse of `columnLetter` in
 * `xlsxPreview.ts`. Kept local rather than imported: this module only ever needs it to decode a
 * `<mergeCell ref>`, and importing it back from `xlsxPreview.ts` (which itself imports THIS
 * module for `XlsxSheetLayout`) would be a circular import for one three-line function. */
function columnLettersToIndex(letters: string): number {
  let n = 0
  for (let i = 0; i < letters.length; i++) n = n * 26 + (letters.charCodeAt(i) - 64)
  return n - 1
}

const CELL_REF_RE = /^([A-Z]+)(\d+)$/

function parseCellRef(ref: string): { row: number; col: number } | null {
  const match = CELL_REF_RE.exec(ref)
  if (!match) return null
  return { row: Number(match[2]) - 1, col: columnLettersToIndex(match[1]) }
}

/** `<mergeCells><mergeCell ref="F1:I1"/>...</mergeCells>` → one `XlsxMerge` per tag. A malformed
 * or single-cell `ref` (no `:`) is skipped rather than guessed at — best-effort, same posture as
 * `xlsxImages.ts`'s relationship walk. */
function parseMergeCells(worksheetXml: string): XlsxMerge[] {
  const merges: XlsxMerge[] = []
  for (const tag of findTags(worksheetXml, 'mergeCell')) {
    const ref = attr(tag, 'ref')
    if (!ref) continue
    const [startRef, endRef] = ref.split(':')
    const start = parseCellRef(startRef)
    const end = endRef ? parseCellRef(endRef) : start
    if (!start || !end) continue
    merges.push({
      row: start.row,
      col: start.col,
      rowSpan: end.row - start.row + 1,
      colSpan: end.col - start.col + 1,
    })
  }
  return merges
}

/** `<cols><col min="2" max="2" width="18.88" customWidth="1"/>...</cols>` → 0-based column index →
 * width. `customWidth="1"` is required — see `XlsxSheetLayout.columnWidths`'s doc for why a `<col>`
 * without it is excluded rather than treated as authored intent. */
function parseColumnWidths(worksheetXml: string): Map<number, number> {
  const widths = new Map<number, number>()
  const colsMatch = /<cols>([\s\S]*?)<\/cols>/.exec(worksheetXml)
  if (!colsMatch) return widths

  for (const tag of findTags(colsMatch[1], 'col')) {
    if (attr(tag, 'customWidth') !== '1') continue
    const width = Number(attr(tag, 'width'))
    const min = Number(attr(tag, 'min'))
    const max = Number(attr(tag, 'max'))
    if (!Number.isFinite(width) || !Number.isFinite(min) || !Number.isFinite(max)) continue
    const lastIndex = Math.min(max - 1, MAX_TRACKED_COLUMN_INDEX)
    for (let index = min - 1; index <= lastIndex; index++) widths.set(index, width)
  }
  return widths
}

/** The floor and ceiling either side of `charWidthToPx`'s conversion. The ceiling matters more in
 * practice: `.xlsheet td`'s own `max-width: 320px` (see `global.css`) already exists specifically
 * because ONE unwrapped long cell would otherwise blow a column out to thousands of pixels — an
 * authored width is a much rarer way to hit the same failure mode (a sheet author fat-fingering a
 * column drag), but the fix is the same cap, so this reuses that exact number rather than picking
 * a new one. The floor keeps a legitimately tiny authored column (a checkbox/serial-number column
 * a few characters wide) from collapsing under this preview's cell padding. */
const MIN_COLUMN_PX = 32
const MAX_COLUMN_PX = 320

/** Excel's own column-width unit is roughly "how many `0` digits of the sheet's default font fit"
 * — this is the standard approximation (used by, among others, SheetJS and openpyxl) back to
 * pixels for a typical UI font: each unit is about 7px, plus 5px of fixed cell padding. Precision
 * doesn't matter here — this only has to get a column's PROPORTIONS right relative to its
 * neighbours, not match Excel's own renderer to the pixel. */
export function charWidthToPx(width: number): number {
  const px = Math.round(width * 7 + 5)
  return Math.min(MAX_COLUMN_PX, Math.max(MIN_COLUMN_PX, px))
}

/**
 * Pure — no fetch, no DOM. Unzips `bytes` (`fflate`, dynamically imported so it only enters the
 * bundle when an xlsx preview actually opens, same as `xlsxImages.ts` and `xlsxPreview.ts`),
 * resolves every sheet name to its worksheet XML, and reads that XML's own `<mergeCells>` and
 * `<cols>` directly — no relationship-chain walk needed beyond the one hop
 * `resolveWorksheetPaths` already does, since both live in the worksheet part itself.
 */
export async function extractXlsxLayouts(bytes: Uint8Array): Promise<XlsxLayoutExtraction> {
  let entries: Record<string, Uint8Array>
  try {
    const { unzipSync } = await import('fflate')
    entries = unzipSync(bytes)
  } catch (error) {
    return {
      status: 'scan_failed',
      detail: error instanceof Error ? error.message : 'Could not read this file as a zip archive.',
    }
  }

  const decoder = new TextDecoder('utf-8')
  const worksheetPaths = resolveWorksheetPaths(entries)
  const layouts = new Map<string, XlsxSheetLayout>()

  for (const [name, path] of worksheetPaths) {
    const worksheetBytes = entries[path]
    if (!worksheetBytes) continue
    const worksheetXml = decoder.decode(worksheetBytes)
    layouts.set(name, { merges: parseMergeCells(worksheetXml), columnWidths: parseColumnWidths(worksheetXml) })
  }

  return { status: 'ok', layouts }
}
